// Admin personal to-do modal.
//
// A small persistent notepad for reminders outside the main projects.
// Modeled on the Kerboom to-do popup, minus category chips
// (Mojo / Kerboom / Personal) — this is a single-app affordance.
//
// Endpoints (all admin-only):
//   GET    /admin/todos
//   POST   /admin/todos           { text, urgency }
//   PATCH  /admin/todos/{id}      { text?, urgency?, status? }
//   DELETE /admin/todos/{id}
//
// Status vocabulary matches the reference screenshot:
//   "todo"        → "TO-DO"
//   "in_progress" → "IN PROGRESS"
//   "completed"   → "Completed"  (collapsible group)
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, ChevronDown, ChevronRight, Circle, Loader2, Plus, Trash2, X } from "lucide-react";

const STATUS = {
  TODO: "todo",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
};
const URGENCY_MAX = 5;

function UrgencyDots({ value, onChange, size = "md", "data-testid": testid }) {
  const px = size === "sm" ? "h-2 w-2" : "h-2.5 w-2.5";
  return (
    <div className="inline-flex items-center gap-1" data-testid={testid}>
      {Array.from({ length: URGENCY_MAX }, (_, i) => {
        const filled = i < value;
        const clickable = typeof onChange === "function";
        return (
          <button
            key={i}
            type="button"
            aria-label={`Urgency ${i + 1} of ${URGENCY_MAX}`}
            disabled={!clickable}
            onClick={() => clickable && onChange(i + 1)}
            className={`rounded-full ${px} ${
              filled ? "bg-orange-500" : "bg-stone-200"
            } ${clickable ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
          />
        );
      })}
    </div>
  );
}

function StatusButton({ status, onCycle, "data-testid": testid }) {
  // Cycles todo → in_progress → completed → todo.
  const meta = {
    [STATUS.TODO]: { label: "To Do", cls: "bg-white border-stone-300 text-stone-700 hover:bg-stone-50" },
    [STATUS.IN_PROGRESS]: { label: "In Progress", cls: "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100" },
    [STATUS.COMPLETED]: { label: "Complete", cls: "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100" },
  }[status];
  const Icon = status === STATUS.COMPLETED ? CheckCircle2 : Circle;
  return (
    <button
      type="button"
      onClick={onCycle}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${meta.cls}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {meta.label}
    </button>
  );
}

function nextStatus(cur) {
  return cur === STATUS.TODO
    ? STATUS.IN_PROGRESS
    : cur === STATUS.IN_PROGRESS
    ? STATUS.COMPLETED
    : STATUS.TODO;
}

function TodoRow({ todo, onUpdate, onDelete }) {
  const completed = todo.status === STATUS.COMPLETED;
  return (
    <div
      className="flex items-center gap-3 py-2 border-b border-stone-100 last:border-b-0"
      data-testid={`todo-row-${todo.id}`}
    >
      <div
        className={`flex-1 text-sm ${completed ? "line-through text-stone-400" : "text-stone-800"}`}
        data-testid={`todo-text-${todo.id}`}
      >
        {todo.text}
      </div>
      <UrgencyDots
        value={todo.urgency}
        onChange={completed ? undefined : (u) => onUpdate(todo.id, { urgency: u })}
        size="sm"
        data-testid={`todo-urgency-${todo.id}`}
      />
      <StatusButton
        status={todo.status}
        onCycle={() => onUpdate(todo.id, { status: nextStatus(todo.status) })}
        data-testid={`todo-status-${todo.id}`}
      />
      <button
        type="button"
        onClick={() => onDelete(todo.id)}
        aria-label="Delete"
        className="text-stone-400 hover:text-red-600 p-1"
        data-testid={`todo-delete-${todo.id}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function Section({ title, count, testid, children, collapsible = false }) {
  const [open, setOpen] = useState(true);
  const bodyId = `todo-section-${testid}-body`;
  return (
    <div className="mb-3" data-testid={`todo-section-${testid}`}>
      <button
        type="button"
        disabled={!collapsible}
        onClick={() => collapsible && setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={bodyId}
        className={`w-full flex items-center justify-between px-1 py-2 border-b border-stone-200 ${
          collapsible ? "cursor-pointer hover:bg-stone-50" : "cursor-default"
        }`}
      >
        <div className="flex items-center gap-2">
          {collapsible &&
            (open ? (
              <ChevronDown className="h-4 w-4 text-stone-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-stone-500" />
            ))}
          <span className="text-xs font-bold uppercase tracking-wide text-stone-700">
            {title}
          </span>
        </div>
        <span className="text-xs text-stone-400" data-testid={`todo-count-${testid}`}>
          {count}
        </span>
      </button>
      {open && (
        <div id={bodyId} className="pt-1">
          {children}
        </div>
      )}
    </div>
  );
}

export default function TodoModal({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [text, setText] = useState("");
  const [urgency, setUrgency] = useState(3);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await api.get("/admin/todos");
        setItems(data.items || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  async function addTodo(e) {
    e?.preventDefault?.();
    const clean = text.trim();
    if (!clean || adding) return;
    setAdding(true);
    setErr("");
    try {
      const { data } = await api.post("/admin/todos", { text: clean, urgency });
      setItems((xs) => [data, ...xs]);
      setText("");
      setUrgency(3);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setAdding(false);
    }
  }

  async function updateTodo(id, patch) {
    // Optimistic update — snap DOM immediately, revert on failure.
    const prev = items;
    setItems((xs) => xs.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    try {
      const { data } = await api.patch(`/admin/todos/${id}`, patch);
      setItems((xs) => xs.map((t) => (t.id === id ? data : t)));
    } catch (e) {
      setItems(prev);
      setErr(e?.response?.data?.detail || e.message);
    }
  }

  async function deleteTodo(id) {
    const prev = items;
    setItems((xs) => xs.filter((t) => t.id !== id));
    try {
      await api.delete(`/admin/todos/${id}`);
    } catch (e) {
      setItems(prev);
      setErr(e?.response?.data?.detail || e.message);
    }
  }

  if (!open) return null;

  const todo = items.filter((t) => t.status === STATUS.TODO);
  const inProgress = items.filter((t) => t.status === STATUS.IN_PROGRESS);
  const completed = items.filter((t) => t.status === STATUS.COMPLETED);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="todo-modal-backdrop"
    >
      <div
        className="mt-14 w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-stone-200"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="todo-modal-title"
        data-testid="todo-modal"
      >
        <div className="p-5 border-b border-stone-100 flex items-start justify-between">
          <div>
            <h2 id="todo-modal-title" className="text-2xl font-bold text-stone-900">
              To-Do
            </h2>
            <p className="text-sm text-stone-500 mt-1">
              Quick reminders for things outside your projects.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded-md hover:bg-stone-100"
            data-testid="todo-modal-close"
          >
            <X className="h-5 w-5 text-stone-500" />
          </button>
        </div>

        <form
          onSubmit={addTodo}
          className="p-5 border-b border-stone-100"
          data-testid="todo-modal-form"
        >
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What needs doing?"
              className="flex-1 rounded-full border border-stone-300 px-4 py-2.5 text-sm outline-none focus:border-stone-500 focus:ring-2 focus:ring-stone-200"
              data-testid="todo-modal-input"
              maxLength={500}
              autoFocus
            />
            <button
              type="submit"
              disabled={!text.trim() || adding}
              className="h-10 w-10 rounded-full bg-stone-700 text-white flex items-center justify-center hover:bg-stone-800 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Add to-do"
              data-testid="todo-modal-add-btn"
            >
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-5 w-5" />}
            </button>
          </div>
          <div className="mt-3 flex items-center justify-end gap-2">
            <span className="text-xs text-stone-500">Urgency:</span>
            <UrgencyDots
              value={urgency}
              onChange={setUrgency}
              data-testid="todo-modal-new-urgency"
            />
          </div>
        </form>

        {err && (
          <div
            className="mx-5 mt-4 rounded-md bg-red-50 border border-red-200 text-red-800 text-xs p-2"
            data-testid="todo-modal-error"
          >
            {err}
          </div>
        )}

        <div className="p-5">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-stone-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <>
              <Section title="To-Do" count={todo.length} testid="todo">
                {todo.length === 0 ? (
                  <div className="text-sm text-stone-400 italic py-2">Nothing pending. Nice.</div>
                ) : (
                  todo.map((t) => (
                    <TodoRow key={t.id} todo={t} onUpdate={updateTodo} onDelete={deleteTodo} />
                  ))
                )}
              </Section>

              <Section title="In Progress" count={inProgress.length} testid="in-progress">
                {inProgress.length === 0 ? (
                  <div className="text-sm text-stone-400 italic py-2">Nothing in flight.</div>
                ) : (
                  inProgress.map((t) => (
                    <TodoRow key={t.id} todo={t} onUpdate={updateTodo} onDelete={deleteTodo} />
                  ))
                )}
              </Section>

              <Section
                title="Completed"
                count={completed.length}
                testid="completed"
                collapsible
              >
                {completed.length === 0 ? (
                  <div className="text-sm text-stone-400 italic py-2">Nothing yet.</div>
                ) : (
                  completed.map((t) => (
                    <TodoRow key={t.id} todo={t} onUpdate={updateTodo} onDelete={deleteTodo} />
                  ))
                )}
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
